import React from 'react';
import { IconButton, Stack, Slider, Tooltip, Typography } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import GetAppIcon from '@mui/icons-material/GetApp';
import ReplayIcon from '@mui/icons-material/Replay';
import SpeedIcon from '@mui/icons-material/Speed';


const AudioControls = ({ 
  audioState, 
  controlHandlers, 
  layout,
  meetingId,
  ServerURL 
}) => {
  const showVolumeSlider = layout.showVolumeSlider;
  const showSpeed = layout.showSpeedControl;
  const showDownload = layout.showDownloadButton;

  const handleDownload = () => {
    const downloadUrl = `${ServerURL}/get_audio/${meetingId}/0/-1`;
    console.log('💾 Downloading from:', downloadUrl);

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `meeting_${meetingId}_audio.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Stack 
      direction="row" 
      spacing={0.5} // Consistent internal spacing
      sx={{ 
        flexShrink: 0, // Don't shrink the group
        alignItems: 'center',
        overflow: 'visible', // Allow slider knob to be visible
        justifyContent: 'flex-end' // Align to right
      }}
    >
      {/* Error Retry Button */}
      {audioState.error && (
        <Tooltip title="Retry loading">
          <IconButton 
            onClick={controlHandlers.handleReload} 
            size="small" 
            color="error" 
            aria-label="Retry"
            sx={{
              '&:hover': { backgroundColor: 'var(--toolbarButtonHoverColor)' }
            }}
          >
            <ReplayIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* Speed Toggle */}
      {showSpeed && (
        <Tooltip title={`Playback speed: ${audioState.playbackRate}x`}>
          <IconButton 
            onClick={controlHandlers.handleSpeedToggle} 
            size="small" 
            aria-label={`Change playback speed (current: ${audioState.playbackRate}x)`}
            sx={{
              color: audioState.playbackRate !== 1 ? 'var(--audioControlButtonColor)' : 'grey.600',
              '&:hover': { backgroundColor: 'var(--toolbarButtonHoverColor)' }
            }}
          >
            <SpeedIcon fontSize="small" />
            <Typography variant="caption" sx={{ ml: 0.5, fontSize: '0.65rem', fontWeight: 'bold' }}>
              {audioState.playbackRate}x
            </Typography>
          </IconButton>
        </Tooltip>
      )}

      {/* Mute Toggle */}
      <IconButton 
        onClick={controlHandlers.handleMuteToggle} 
        size="small" 
        aria-label={audioState.isMuted ? 'Unmute' : 'Mute'}
        sx={{
          color: 'var(--audioControlButtonColor)',
          '&:hover': { backgroundColor: 'var(--toolbarButtonHoverColor)' }
        }}
      >
        {audioState.isMuted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
      </IconButton>

      {/* Volume Slider - Smoothly appears/disappears */}
      <Slider
        value={audioState.isMuted ? 0 : audioState.volume}
        onChange={controlHandlers.handleVolumeChange}
        min={0}
        max={1}
        step={0.1}
        sx={{ 
          width: layout.volumeSliderWidth,
          minWidth: layout.volumeSliderWidth,
          maxWidth: layout.volumeSliderWidth,
          flexShrink: 3, // Can shrink significantly
          transition: 'width 0.2s ease, opacity 0.2s ease',
          opacity: layout.volumeSliderWidth > 0 ? 1 : 0,
          overflow: 'visible', // Allow knob to be visible at edges
          mx: 0.5, // Add small margin left and right
          px: 1 // Add padding to prevent knob from being cut off
        }}
        size="small"
      />

      {/* Download Button - Always visible */}
      <Tooltip title="Download audio">
        <IconButton 
          onClick={handleDownload} 
          size="small" 
          aria-label="Download audio"
          sx={{
            flexShrink: 0, // Don't shrink download button
            color: 'var(--audioControlButtonColor)',
            '&:hover': { backgroundColor: 'var(--toolbarButtonHoverColor)' }
          }}
        >
          <GetAppIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

export default AudioControls;
